import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Form, FormControl, FormField, FormItem, FormLabel, FormMessage, Input } from '@/components/ui';
import { createFitnessCenter } from '../../../api';
import { FitnessCenterStatus } from '@/models'
import DaumPostcode, { Address, useDaumPostcodePopup } from 'react-daum-postcode';

const CoopContactForm: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [postNumber, setPostNumber] = useState<string>('');
  const [address, setAddress] = useState<string>('');

  const open = useDaumPostcodePopup('https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js');

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      centerName: '',
      centerPhone: '',
      ceoName: '',
      businessNumber: '',
      email: '',
      postNumber: '',
      basicAddress: '',
      detailAddress: '',
      status: FitnessCenterStatus.PENDING,
    },
  });

  const handleComplete = (data: Address) => {
    let fullAddress = data.address;
    let extraAddress = '';

    if (data.addressType === 'R') {
      if (data.bname !== '') {
        extraAddress += data.bname;
      }
      if (data.buildingName !== '') {
        extraAddress += extraAddress !== '' ? `, ${data.buildingName}` : data.buildingName;
      }
      fullAddress += extraAddress !== '' ? ` (${extraAddress})` : '';
    }

    setPostNumber(data.zonecode);
    setAddress(fullAddress);
    
    // 폼 데이터도 업데이트
    form.setValue('postNumber', data.zonecode);
    form.setValue('basicAddress', fullAddress);
    
    setIsOpen(false);
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      await createFitnessCenter({ 
        ...values, 
        postNumber: parseInt(values.postNumber || '0'),
        centerPhone: values.centerPhone || '',
        ceoName: values.ceoName || '',
        businessNumber: values.businessNumber || '',
        basicAddress: values.basicAddress || '',
        detailAddress: values.detailAddress || ''
      });
    } catch {
      alert('파트너스 신청에 실패했습니다.');
      return;
    }
    alert('파트너스 신청이 완료되었습니다');

    setIsOpen(false);
    setPostNumber('');
    setAddress('');
    form.reset({});
  };

  const handleClick = () => {
    open({ onComplete: handleComplete });
  };

  if (isOpen) {
    return (
      <div className="fixed top-0 left-0 w-full h-full bg-black/50 z-50">
        <DaumPostcode onComplete={handleComplete} />
      </div>
    );
  }

  return (
    <section id="coop-contact-form">
      <div className="container px-10 py-24">
        <h4 className="text-[2.0rem] font-extrabold text-center">카운트핏 파트너스 (협력 파트너스 센터) 모집</h4>
        <p className="mt-12 text-center font-medium">
          카운트핏 파트너스 혜택 :
          <br className="lg:block hidden" />
          <br className="lg:block hidden" />
          1. 카운트핏 신규 구독회원 1개월 무료 제공
          <br className="lg:block hidden" />
          <span className="font-light">(3개월 기본 구독료 중 1개월 구독료 환급)</span>
          <br className="lg:block hidden" />
          <br className="lg:block hidden" />
          2. 회원에게 제공되는 모션인식밴드와 무선충전 거치대에 헬스장 상호 인쇄
          <br className="lg:block hidden" />
          <span className="font-light">(카운트핏 연결회원 10명 초과시 그 이후 연결회원부터 적용)</span>
        </p>
        <div className="w-[10%] mx-auto mt-16 border-b border-gray-400" />
        <div className="flex flex-col items-center">
          <div className="mt-16  lg:w-[35%] md:w-[50%] sm:w-[70%] w-[90%]">
            <Form {...form}>
              <form className="flex flex-col gap-2" onSubmit={form.handleSubmit(onSubmit)}>
                <FormField
                  control={form.control}
                  name="centerName"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-1 relative">
                      <p className="text-red-500 absolute left-[-10px]">*</p>
                        <FormLabel>헬스장 또는 트레이너 이름</FormLabel>
                      </div>
                      <FormControl>
                        <Input placeholder="카운트핏" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-1 relative">
                        <p className="text-red-500 absolute left-[-10px]">*</p>
                        <FormLabel>이메일</FormLabel>
                      </div>
                      <FormControl>
                        <Input placeholder="count-fit@naver.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="ceoName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>대표자 이름</FormLabel>
                      <FormControl>
                        <Input placeholder="홍길동" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="centerPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>전화번호</FormLabel>
                      <FormControl>
                        <Input placeholder="01012345678" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="postNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>우편번호</FormLabel>
                      <Button type="button" size="lg" className="ml-4" onClick={handleClick}>주소 검색</Button>
                      <FormControl>
                        <Input placeholder="51353" {...field} value={postNumber} readOnly />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="basicAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>주소</FormLabel>
                      <FormControl>
                        <Input placeholder="경기도 수원시 영통구 광교로 156" {...field} value={address} readOnly />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="detailAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>상세 주소</FormLabel>
                      <FormControl>
                        <Input placeholder="6층 2호" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="businessNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>사업자번호</FormLabel>
                      <FormControl>
                        <Input placeholder="000-00-00000" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <address className="mt-4 not-italic text-[0.8rem] text-foreground-accent text-center text-gray-500">
                  <p>하단의 제출하기 버튼을 클릭하시면</p>
                  <p>설명자료 및 파트너스 가입 링크를 보내드립니다.</p>
                </address>
                <Button type="submit" className="mt-8 w-full">
                  제출하기
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </section>
  );
};

const formSchema = z.object({
  centerName: z.string({ message: '헬스장 및 트레이너 이름을 입력해주세요.' }).min(1, '올바른 헬스장 및 트레이너 이름을 입력해주세요.'),
  centerPhone: z.string().optional(),
  ceoName: z.string().optional(),
  email: z.string({ message: '이메일을 입력해주세요.' }).regex(/^[^@]+@[^@]+\.[^@]+$/, '올바른 이메일을 입력해주세요.'),
  businessNumber: z.string().optional(),
  postNumber: z.string().optional(),
  basicAddress: z.string().optional(),
  detailAddress: z.string().optional(),
  status: z.number(),
});

export default CoopContactForm;
